/**
 * Checks if a new version of the application is available
 * by comparing the buildTime from the server with the stored buildTime.
 * buildTime updates with every deployment, making it a reliable indicator of new builds.
 * @param {string} storedBuildTime - Previously stored buildTime to compare against
 * @returns {Promise<{updateAvailable: boolean, newBuildTime?: string, newVersion?: string}>}
 */
export const checkForUpdates = async (storedBuildTime = null) => {
  try {
    const response = await fetch("/version.json", { cache: "no-store" });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    const serverBuildTime = data.buildTime;
    const serverVersion = data.version;
    const releaseNote = data.releaseNote || '';
    const description = data.description || '';

    // Compare buildTime - updates on every deployment regardless of version
    const isUpdateAvailable = storedBuildTime && serverBuildTime !== storedBuildTime;

    return {
      updateAvailable: isUpdateAvailable,
      newBuildTime: serverBuildTime,
      newVersion: serverVersion,
      serverVersion: serverVersion,
      releaseNote: releaseNote,
      description: description,
    };
  } catch (error) {
    console.error("Error checking for updates:", error);
    return {
      updateAvailable: false,
      newBuildTime: null,
      newVersion: undefined,
      releaseNote: '',
      description: '',
    };
  }
};

/**
 * Starts a periodic check for updates at the specified interval
 * Uses buildTime to detect deployments (works even if version number doesn't change)
 * @param {number} intervalMs - Interval in milliseconds (default: 60000 = 60 seconds)
 * @param {function} onUpdateAvailable - Callback when update is available
 * @returns {?number} - Timer ID to clear the interval later
 */
export const startVersionCheck = (intervalMs = 60000, onUpdateAvailable = null) => {
  let storedBuildTime = null;

  // Check immediately on start to get initial buildTime
  checkForUpdates(storedBuildTime).then((result) => {
    // Store the initial buildTime for future comparisons
    storedBuildTime = result.newBuildTime;
  });

  // Set up periodic checks
  const timerId = setInterval(async () => {
    const result = await checkForUpdates(storedBuildTime);
    
    if (result.updateAvailable && onUpdateAvailable) {
      // Update stored buildTime after detecting new deployment
      storedBuildTime = result.newBuildTime;
      onUpdateAvailable(result);
    }
  }, intervalMs);

  return timerId;
};

export default checkForUpdates;
